import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { anfrage, betriebLogoHochladen } from "./client";
import type {
  Anlage,
  AnlageAktualisieren,
  AnlageListenEintrag,
  AnlageStatus,
  AnmeldungVorbereitenErgebnis,
  Bestaetigung,
  Betrieb,
  BetriebListenEintrag,
  BetriebLoeschVorschau,
  BetriebStammdaten,
  Dokument,
  Einladung,
  FrageTyp,
  Kunde,
  KundeLoeschVorschau,
  KundenLink,
  MastrAktualisieren,
  MastrDaten,
  NetzbetreiberErmittlung,
  NetzbetreiberZugang,
  NachreichungEntscheidung,
  PortalAnlageDetail,
  PortalBenutzer,
  PortalKundeErstellen,
  PortalKundenLinkStand,
  PortalStart,
  PortalUebersicht,
  Seite,
  StatusLinkAusgabe,
  VollmachtModell,
} from "./typen";

const EIGENE_FEHLERMELDUNG = { eigeneFehlermeldung: true } as const;

export const schluessel = {
  kunden: ["kunden"] as const,
  anlagen: (filter: object) => ["anlagen", filter] as const,
  anlagenAlle: ["anlagen"] as const,
  anlage: (id: number) => ["anlage", id] as const,
  mastr: (id: number) => ["mastr", id] as const,
  betriebe: ["betriebe"] as const,
  betrieb: (id: number) => ["betriebe", id] as const,
  portalStart: ["portal", "start"] as const,
  portalUebersicht: ["portal", "uebersicht"] as const,
  portalAnlage: (id: number) => ["portal", "anlage", id] as const,
};

export function useKunden() {
  return useQuery({
    queryKey: schluessel.kunden,
    queryFn: ({ signal }) => anfrage<Seite<Kunde>>("/api/kunden?limit=200", { signal }),
  });
}

export function useKundeAnlegen() {
  const client = useQueryClient();
  return useMutation({
    meta: EIGENE_FEHLERMELDUNG,
    mutationFn: (daten: { name: string; email: string }) => anfrage<Kunde>("/api/kunden", { methode: "POST", body: daten }),
    onSuccess: () => client.invalidateQueries({ queryKey: schluessel.kunden }),
  });
}

export interface AnlagenFilter {
  status?: AnlageStatus;
  kundeId?: number;
  neueUnterlagen?: boolean;
  limit: number;
  offset: number;
}

export function useAnlagen(filter: AnlagenFilter, aktiv = true) {
  const parameter = new URLSearchParams({ limit: String(filter.limit), offset: String(filter.offset) });
  if (filter.status) parameter.set("status", filter.status);
  if (filter.kundeId) parameter.set("kunde_id", String(filter.kundeId));
  if (filter.neueUnterlagen) parameter.set("neue_unterlagen", "true");
  return useQuery({
    queryKey: schluessel.anlagen(filter),
    queryFn: ({ signal }) => anfrage<Seite<AnlageListenEintrag>>(`/api/anlagen?${parameter}`, { signal }),
    placeholderData: keepPreviousData,
    enabled: aktiv,
  });
}

export function useAnlage(id: number) {
  return useQuery({
    queryKey: schluessel.anlage(id),
    queryFn: ({ signal }) => anfrage<Anlage>(`/api/anlagen/${id}`, { signal }),
    enabled: Number.isFinite(id) && id > 0,
  });
}

function useAnlageAktualisiert() {
  const client = useQueryClient();
  return (anlage: Anlage) => {
    client.setQueryData(schluessel.anlage(anlage.id), anlage);
    void client.invalidateQueries({ queryKey: schluessel.anlagenAlle });
  };
}

export function useAnlageSpeichern(id: number) {
  const aktualisiert = useAnlageAktualisiert();
  return useMutation({
    meta: EIGENE_FEHLERMELDUNG,
    mutationFn: (body: AnlageAktualisieren) => anfrage<Anlage>(`/api/anlagen/${id}`, { methode: "PUT", body }),
    onSuccess: aktualisiert,
  });
}

export function useAnlageBestaetigen(id: number) {
  const aktualisiert = useAnlageAktualisiert();
  return useMutation({
    meta: EIGENE_FEHLERMELDUNG,
    mutationFn: () => anfrage<Bestaetigung>(`/api/anlagen/${id}/bestaetigen`, { methode: "POST" }),
    onSuccess: (ergebnis) => aktualisiert(ergebnis.anlage),
  });
}

export function useAnlageAblehnen(id: number) {
  const aktualisiert = useAnlageAktualisiert();
  return useMutation({
    meta: EIGENE_FEHLERMELDUNG,
    mutationFn: (body: { grund: string; neu_extrahieren: boolean }) =>
      anfrage<Anlage>(`/api/anlagen/${id}/ablehnen`, { methode: "POST", body }),
    onSuccess: aktualisiert,
  });
}

export function useInBearbeitungSetzen(id: number) {
  const aktualisiert = useAnlageAktualisiert();
  return useMutation({
    meta: EIGENE_FEHLERMELDUNG,
    mutationFn: (wert: boolean) => anfrage<Anlage>(`/api/anlagen/${id}/in-bearbeitung`, { methode: "PUT", body: { wert } }),
    onSuccess: aktualisiert,
  });
}

/** Liefert immer denselben Link, solange einer aktiv ist – erzeugt nur beim ersten Mal einen neuen. */
export function useAnlageStatusLinkAnzeigen(id: number) {
  const client = useQueryClient();
  return useMutation({
    meta: EIGENE_FEHLERMELDUNG,
    mutationFn: () => anfrage<StatusLinkAusgabe>(`/api/anlagen/${id}/status-link`, { methode: "POST" }),
    onSuccess: () => void client.invalidateQueries({ queryKey: schluessel.anlage(id) }),
  });
}

/** Macht einen vorhandenen Link sofort ungültig und erzeugt einen neuen – bewusste, seltene Aktion. */
export function useAnlageStatusLinkErneuern(id: number) {
  const client = useQueryClient();
  return useMutation({
    meta: EIGENE_FEHLERMELDUNG,
    mutationFn: () => anfrage<StatusLinkAusgabe>(`/api/anlagen/${id}/status-link/erneuern`, { methode: "POST" }),
    onSuccess: () => void client.invalidateQueries({ queryKey: schluessel.anlage(id) }),
  });
}

export function useAnlageStatusLinkSperren(id: number) {
  const aktualisiert = useAnlageAktualisiert();
  return useMutation({
    meta: EIGENE_FEHLERMELDUNG,
    mutationFn: () => anfrage<Anlage>(`/api/anlagen/${id}/status-link/sperren`, { methode: "POST" }),
    onSuccess: aktualisiert,
  });
}

export function useExtraktionWiederholen() {
  const client = useQueryClient();
  return useMutation({
    meta: EIGENE_FEHLERMELDUNG,
    mutationFn: (dokumentId: number) =>
      anfrage<Dokument>(`/api/dokumente/${dokumentId}/extraktion-wiederholen`, { methode: "POST" }),
    onSuccess: (dokument) => {
      if (dokument.anlage_id) void client.invalidateQueries({ queryKey: schluessel.anlage(dokument.anlage_id) });
      void client.invalidateQueries({ queryKey: schluessel.anlagenAlle });
    },
  });
}

export function useMastr(id: number) {
  return useQuery({
    queryKey: schluessel.mastr(id),
    queryFn: ({ signal }) => anfrage<MastrDaten>(`/api/anlagen/${id}/mastr`, { signal }),
    enabled: Number.isFinite(id) && id > 0,
  });
}

export function useMastrSpeichern(id: number) {
  const client = useQueryClient();
  return useMutation({
    meta: EIGENE_FEHLERMELDUNG,
    mutationFn: (body: MastrAktualisieren & { version?: number }) =>
      anfrage<MastrDaten>(`/api/anlagen/${id}/mastr`, { methode: "PUT", body }),
    onSuccess: (daten) => {
      client.setQueryData(schluessel.mastr(id), daten);
      void client.invalidateQueries({ queryKey: schluessel.anlage(id) });
      void client.invalidateQueries({ queryKey: schluessel.anlagenAlle });
    },
  });
}

export function useNetzbetreiberErmitteln(id: number) {
  const client = useQueryClient();
  return useMutation({
    meta: EIGENE_FEHLERMELDUNG,
    mutationFn: () => anfrage<NetzbetreiberErmittlung>(`/api/anlagen/${id}/netzbetreiber-ermitteln`, { methode: "POST" }),
    onSuccess: () => void client.invalidateQueries({ queryKey: schluessel.mastr(id) }),
  });
}

export function useAnmeldungVorbereiten() {
  return useMutation({
    meta: EIGENE_FEHLERMELDUNG,
    mutationFn: (ids: number[]) =>
      anfrage<AnmeldungVorbereitenErgebnis>("/api/anlagen/anmeldung-vorbereiten", { methode: "POST", body: { ids } }),
  });
}

// ------------------------------------------------------ Portal: Betriebe & Zugänge

export function useBetriebe() {
  return useQuery({
    queryKey: schluessel.betriebe,
    queryFn: ({ signal }) => anfrage<BetriebListenEintrag[]>("/api/betriebe", { signal }),
  });
}

export function useBetrieb(id: number) {
  return useQuery({
    queryKey: schluessel.betrieb(id),
    queryFn: ({ signal }) => anfrage<Betrieb>(`/api/betriebe/${id}`, { signal }),
    enabled: Number.isInteger(id) && id > 0,
  });
}

/** Nach jeder Änderung: Detail direkt übernehmen, Liste (Zähler) neu laden. */
function useBetriebAktualisiert() {
  const client = useQueryClient();
  return (betrieb: Betrieb) => {
    client.setQueryData(schluessel.betrieb(betrieb.id), betrieb);
    void client.invalidateQueries({ queryKey: schluessel.betriebe, exact: true });
  };
}

export function useBetriebAnlegen() {
  const aktualisiert = useBetriebAktualisiert();
  return useMutation({
    meta: EIGENE_FEHLERMELDUNG,
    mutationFn: (daten: Partial<BetriebStammdaten> & { name: string }) =>
      anfrage<Betrieb>("/api/betriebe", { methode: "POST", body: daten }),
    onSuccess: aktualisiert,
  });
}

export function useBetriebSpeichern(id: number) {
  const aktualisiert = useBetriebAktualisiert();
  return useMutation({
    meta: EIGENE_FEHLERMELDUNG,
    mutationFn: (daten: Partial<BetriebStammdaten> & { aktiv?: boolean; impressum_url?: string | null; datenschutz_url?: string | null }) =>
      anfrage<Betrieb>(`/api/betriebe/${id}`, { methode: "PUT", body: daten }),
    onSuccess: aktualisiert,
  });
}

export function useZugaengeSpeichern(id: number) {
  const aktualisiert = useBetriebAktualisiert();
  return useMutation({
    meta: EIGENE_FEHLERMELDUNG,
    mutationFn: (zugaenge: NetzbetreiberZugang[]) =>
      anfrage<Betrieb>(`/api/betriebe/${id}/zugaenge`, { methode: "PUT", body: zugaenge }),
    onSuccess: aktualisiert,
  });
}

export function useInstallateurEinladen(betriebId: number) {
  const client = useQueryClient();
  return useMutation({
    meta: EIGENE_FEHLERMELDUNG,
    mutationFn: (daten: { name: string; email: string }) =>
      anfrage<Einladung>(`/api/betriebe/${betriebId}/benutzer`, { methode: "POST", body: daten }),
    onSuccess: () => client.invalidateQueries({ queryKey: schluessel.betriebe }),
  });
}

export function useZugangslinkErneuern(betriebId: number) {
  const client = useQueryClient();
  return useMutation({
    meta: EIGENE_FEHLERMELDUNG,
    mutationFn: (benutzerId: number) =>
      anfrage<Einladung>(`/api/betriebe/${betriebId}/benutzer/${benutzerId}/zugangslink`, { methode: "POST" }),
    onSuccess: () => client.invalidateQueries({ queryKey: schluessel.betrieb(betriebId) }),
  });
}

export function useInstallateurSperren(betriebId: number) {
  const client = useQueryClient();
  return useMutation({
    meta: EIGENE_FEHLERMELDUNG,
    mutationFn: ({ benutzerId, aktiv }: { benutzerId: number; aktiv: boolean }) =>
      anfrage<PortalBenutzer>(`/api/betriebe/${betriebId}/benutzer/${benutzerId}`, { methode: "PUT", body: { aktiv } }),
    onSuccess: () => client.invalidateQueries({ queryKey: schluessel.betrieb(betriebId) }),
  });
}

export function usePortalStart() {
  return useQuery({
    queryKey: schluessel.portalStart,
    queryFn: ({ signal }) => anfrage<PortalStart>("/api/portal/start", { signal }),
  });
}

// ---------------------------------------------------- Portal: Anmeldungen und Uploads

export function usePortalUebersicht() {
  return useQuery({
    queryKey: schluessel.portalUebersicht,
    queryFn: ({ signal }) => anfrage<PortalUebersicht>("/api/portal/uebersicht", { signal }),
  });
}

export function usePortalAnlage(id: number) {
  return useQuery({
    queryKey: schluessel.portalAnlage(id),
    queryFn: ({ signal }) => anfrage<PortalAnlageDetail>(`/api/portal/anlagen/${id}`, { signal }),
    enabled: Number.isFinite(id) && id > 0,
  });
}

export function usePortalAnmeldungStarten() {
  const client = useQueryClient();
  return useMutation({
    meta: EIGENE_FEHLERMELDUNG,
    mutationFn: (daten: PortalKundeErstellen) => anfrage<PortalAnlageDetail>("/api/portal/kunden", { methode: "POST", body: daten }),
    onSuccess: (anlage) => {
      client.setQueryData(schluessel.portalAnlage(anlage.id), anlage);
      void client.invalidateQueries({ queryKey: schluessel.portalUebersicht });
    },
  });
}

function usePortalAnlageAktualisiert() {
  const client = useQueryClient();
  return (anlage: PortalAnlageDetail) => {
    client.setQueryData(schluessel.portalAnlage(anlage.id), anlage);
    void client.invalidateQueries({ queryKey: schluessel.portalUebersicht });
  };
}

/** Nach einem Upload: Dokumentliste und Übersicht neu laden. */
export function usePortalNachUpload(anlageId: number) {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({ queryKey: schluessel.portalAnlage(anlageId) });
    void client.invalidateQueries({ queryKey: schluessel.portalUebersicht });
  };
}

export function usePortalEinreichen(anlageId: number) {
  const aktualisiert = usePortalAnlageAktualisiert();
  return useMutation({
    meta: EIGENE_FEHLERMELDUNG,
    mutationFn: () => anfrage<PortalAnlageDetail>(`/api/portal/anlagen/${anlageId}/einreichen`, { methode: "POST" }),
    onSuccess: aktualisiert,
  });
}

export function usePortalEntfernen() {
  const aktualisiert = usePortalAnlageAktualisiert();
  return useMutation({
    meta: EIGENE_FEHLERMELDUNG,
    mutationFn: (dokumentId: number) => anfrage<PortalAnlageDetail>(`/api/portal/dokumente/${dokumentId}/entfernen`, { methode: "POST" }),
    onSuccess: aktualisiert,
  });
}

/** Liefert immer denselben Link, solange einer aktiv ist – kann den Kunden nie aussperren. */
export function usePortalStatusLinkAnzeigen(anlageId: number) {
  const client = useQueryClient();
  return useMutation({
    meta: EIGENE_FEHLERMELDUNG,
    mutationFn: () => anfrage<StatusLinkAusgabe>(`/api/portal/anlagen/${anlageId}/status-link`, { methode: "POST" }),
    onSuccess: () => void client.invalidateQueries({ queryKey: schluessel.portalAnlage(anlageId) }),
  });
}

// ---------------------------------------------- Backoffice: Nachreichungen, Zurücknehmen

export function useNachreichungEntscheiden(anlageId: number) {
  const client = useQueryClient();
  return useMutation({
    meta: EIGENE_FEHLERMELDUNG,
    mutationFn: ({ dokumentId, entscheidung }: { dokumentId: number; entscheidung: NachreichungEntscheidung }) =>
      anfrage<Dokument>(`/api/dokumente/${dokumentId}/nachreichung`, { methode: "POST", body: entscheidung }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: schluessel.anlage(anlageId) });
      void client.invalidateQueries({ queryKey: schluessel.anlagenAlle });
    },
  });
}

export function useDokumentZuruecknehmen(anlageId: number) {
  const client = useQueryClient();
  return useMutation({
    meta: EIGENE_FEHLERMELDUNG,
    mutationFn: ({ dokumentId, grund, bestaetigt }: { dokumentId: number; grund?: string; bestaetigt: boolean }) =>
      anfrage<Dokument>(`/api/dokumente/${dokumentId}/zuruecknehmen`, { methode: "POST", body: { grund: grund || null, bestaetigt } }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: schluessel.anlage(anlageId) });
      void client.invalidateQueries({ queryKey: schluessel.anlagenAlle });
    },
  });
}

// ---------------------------------------------- Backoffice: endgültig löschen (nur Admin)

export function useKundeLoeschvorschau(kundeId: number, aktiv: boolean) {
  return useQuery({
    queryKey: ["loeschvorschau", "kunde", kundeId],
    queryFn: ({ signal }) => anfrage<KundeLoeschVorschau>(`/api/kunden/${kundeId}/loeschvorschau`, { signal }),
    enabled: aktiv,
    gcTime: 0,
  });
}

export function useKundeLoeschen(kundeId: number) {
  const client = useQueryClient();
  return useMutation({
    meta: EIGENE_FEHLERMELDUNG,
    mutationFn: (bestaetigung: string) =>
      anfrage<KundeLoeschVorschau>(`/api/kunden/${kundeId}/loeschen`, { methode: "POST", body: { bestaetigung } }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: schluessel.kunden });
      void client.invalidateQueries({ queryKey: schluessel.anlagenAlle });
      void client.invalidateQueries({ queryKey: schluessel.betriebe });
    },
  });
}

export function useDokumentLoeschen(anlageId: number) {
  const client = useQueryClient();
  return useMutation({
    meta: EIGENE_FEHLERMELDUNG,
    mutationFn: ({ dokumentId, bestaetigt }: { dokumentId: number; bestaetigt: boolean }) =>
      anfrage<null>(`/api/dokumente/${dokumentId}/loeschen`, { methode: "POST", body: { bestaetigt } }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: schluessel.anlage(anlageId) });
      void client.invalidateQueries({ queryKey: schluessel.anlagenAlle });
    },
  });
}

export function useBetriebLoeschvorschau(betriebId: number, aktiv: boolean) {
  return useQuery({
    queryKey: ["loeschvorschau", "betrieb", betriebId],
    queryFn: ({ signal }) => anfrage<BetriebLoeschVorschau>(`/api/betriebe/${betriebId}/loeschvorschau`, { signal }),
    enabled: aktiv,
    gcTime: 0,
  });
}

export function useBetriebLoeschen(betriebId: number) {
  const client = useQueryClient();
  return useMutation({
    meta: EIGENE_FEHLERMELDUNG,
    mutationFn: (bestaetigung: string) =>
      anfrage<null>(`/api/betriebe/${betriebId}/loeschen`, { methode: "POST", body: { bestaetigung } }),
    onSuccess: () => void client.invalidateQueries({ queryKey: schluessel.betriebe }),
  });
}

// ---------------------------------------------- Kunden-Link (Angaben, Vollmacht, Rückfragen)

export function useKundenLinks(anlageId: number) {
  return useQuery({
    queryKey: ["kunden-links", anlageId],
    queryFn: ({ signal }) => anfrage<KundenLink[]>(`/api/anlagen/${anlageId}/kunden-links`, { signal }),
  });
}

export function useKundenLinkErzeugen(anlageId: number) {
  const client = useQueryClient();
  return useMutation({
    meta: EIGENE_FEHLERMELDUNG,
    mutationFn: (body: { art: "erfassung" | "rueckfrage"; vollmacht_modell: VollmachtModell | null; fragen: { text: string; typ: FrageTyp }[] }) =>
      anfrage<KundenLink>(`/api/anlagen/${anlageId}/kunden-links`, { methode: "POST", body }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ["kunden-links", anlageId] }),
  });
}

export function useKundenLinkSperren(anlageId: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (linkId: number) => anfrage<KundenLink>(`/api/kunden-links/${linkId}/sperren`, { methode: "POST" }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ["kunden-links", anlageId] }),
  });
}

export function usePortalKundenLink(anlageId: number) {
  return useQuery({
    queryKey: ["portal", "kunden-link", anlageId],
    queryFn: ({ signal }) => anfrage<PortalKundenLinkStand>(`/api/portal/anlagen/${anlageId}/kunden-link`, { signal }),
  });
}

export function usePortalKundenLinkErzeugen(anlageId: number) {
  const client = useQueryClient();
  return useMutation({
    meta: EIGENE_FEHLERMELDUNG,
    mutationFn: (vollmacht_modell: VollmachtModell | null) =>
      anfrage<PortalKundenLinkStand>(`/api/portal/anlagen/${anlageId}/kunden-link`, { methode: "POST", body: { vollmacht_modell } }),
    onSuccess: (stand) => client.setQueryData(["portal", "kunden-link", anlageId], stand),
  });
}

// ---------------------------------------------- Individueller Status, Auftritt des Betriebs

export function useStatusIndividuell(anlageId: number) {
  const client = useQueryClient();
  return useMutation({
    meta: EIGENE_FEHLERMELDUNG,
    mutationFn: (text: string | null) =>
      anfrage<Anlage>(`/api/anlagen/${anlageId}/status-individuell`, { methode: "PUT", body: { text } }),
    onSuccess: (anlage) => {
      client.setQueryData(schluessel.anlage(anlageId), anlage);
      void client.invalidateQueries({ queryKey: schluessel.anlagenAlle });
    },
  });
}

export function useBetriebLogo(betriebId: number) {
  const client = useQueryClient();
  const fertig = (betrieb: Betrieb) => {
    client.setQueryData(schluessel.betrieb(betriebId), betrieb);
    void client.invalidateQueries({ queryKey: ["betrieb-logo", betriebId] });
  };
  const hochladen = useMutation({ meta: EIGENE_FEHLERMELDUNG, mutationFn: (datei: File) => betriebLogoHochladen(betriebId, datei), onSuccess: fertig });
  const entfernen = useMutation({
    meta: EIGENE_FEHLERMELDUNG,
    mutationFn: () => anfrage<Betrieb>(`/api/betriebe/${betriebId}/logo/entfernen`, { methode: "POST" }),
    onSuccess: fertig,
  });
  return { hochladen, entfernen };
}
